import {useCallback, useState} from "react";
import {http} from "../../../services/api";

// On-demand generation goes through the Java backend (a direct Lambda invoke),
// which is not subject to API Gateway's 29s integration timeout — so large
// packets complete without a 504 and no retry is needed.

export const useGeneratePdf = ({
  pdfDocumentName,
  admissionId,
  hasScrollEnforcementFeatureFlag,
}: {
  pdfDocumentName: string;
  admissionId: string | null;
  hasScrollEnforcementFeatureFlag: boolean;
}) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [file, setFile] = useState<File | null>(null);

  const generatePdf = useCallback(async () => {
    if (!hasScrollEnforcementFeatureFlag || isGenerating || !admissionId)
      return;

    setIsGenerating(true);
    try {
      // Step 1: Generate the PDF (via the Java backend) and get the presigned URL.
      const lambdaResponse = await http.get(
        `/v3/workflows/${admissionId}/download-url`,
        {type: "full"},
      );
      const downloadUrl = (lambdaResponse.data as {downloadUrl?: string})
        ?.downloadUrl;
      if (!downloadUrl) {
        throw new Error("Download URL not found in response");
      }

      // Step 2: Fetch the actual PDF bytes from S3 via the presigned URL
      const pdfResponse = await fetch(downloadUrl);
      if (!pdfResponse.ok) {
        throw new Error("Failed to fetch PDF from S3");
      }

      const blob = await pdfResponse.blob();
      const file = new File([blob], pdfDocumentName, {type: "application/pdf"});

      setFile(file);
    } catch (error) {
      setFile(null);
    } finally {
      setIsGenerating(false);
    }
  }, [
    admissionId,
    pdfDocumentName,
    isGenerating,
    hasScrollEnforcementFeatureFlag,
  ]);

  return {
    isGenerating,
    file,
    generatePdf,
  };
};

export const downloadPdf = (file: File, pdfDocumentName: string) => {
  if (!file) return;

  const url = window.URL.createObjectURL(file as File);
  const downloadLink = document.createElement("a");
  downloadLink.href = url;
  downloadLink.download = pdfDocumentName;
  document.body.appendChild(downloadLink);
  downloadLink.click();
  document.body.removeChild(downloadLink);
  window.URL.revokeObjectURL(url);
};
